/**
 * Main Application Component
 * Entry point for the Parlay Picker application
 */

import { DisputeBanner } from '@components/ui/DisputeBanner';
import { ShareParlayModal } from '@components/ui/ShareParlayModal';
import { initErrorTracking } from './services/errorTracking';

// Initialize error tracking on app start
initErrorTracking();

import { useCurrentLeagueKey } from '@hooks/useCurrentLeagueKey';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useLeagueSportsbook } from '@hooks/useLeagueSportsbook';
import type { AnalysisLevel, AnalyticsFilter } from '@parlay-picker/shared/analyticsScopes';
import { LOGOUT_REASON_MESSAGES } from '@parlay-picker/shared/authMessages';
import { useChatStore } from '@stores/chatStore';
import { useParlayBuilderStore } from '@stores/parlayBuilderStore';
import { AlertTriangle, PauseCircle } from 'lucide-react';
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { AppHeader } from './components/AppHeader';
import { AppVersionBanner } from './components/AppVersionBanner';
import { ChatFloatingButton } from './components/chat/ChatFloatingButton';
import { ChatPanel } from './components/chat/ChatPanel';
import { ChatTrackContext } from './components/chat/chatTrackContext';
import { EmailVerificationGate } from './components/EmailVerificationGate';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ParlayBuilderPanel } from './components/parlay-builder';
import { ThemeSync } from './components/ThemeSync';
import { GlobalModals, LegalReAcceptanceModal, MobileNav, OnboardingModal } from './components/ui';
import { QuotaCoinNotification } from './components/ui/QuotaCoinNotification';
import { ToastContainer } from './components/ui/ToastContainer';
import { UserProfileModal } from './components/ui/UserProfileModal';
import { apiUrl } from './config/api';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { OpenParlayContext } from './contexts/OpenParlayContext';
import { QuotaCoinProvider } from './contexts/QuotaCoinContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { useBets } from './hooks/useBets';
import { useBreakdownPerformance } from './hooks/useBreakdownPerformance';
import { useFeatureFlags } from './hooks/useFeatureFlags';
import { useLegs } from './hooks/useLegs';
import { useNotificationLink } from './hooks/useNotificationLink';
import { useSharing } from './hooks/useSharing';
import { type UseTrackedParlaysResult, useTrackedParlays } from './hooks/useTrackedParlays';
import { useVersionCheck } from './hooks/useVersionCheck';
import { useOptimizerStore } from './stores/optimizerStore';
import { ANALYTICS_TABS, PRIMARY_TABS, type TabId } from './types/tabs';
import {
  calculateBetStats,
  calculateLegStats,
  groupByLeague,
  groupByLegs,
  groupByOdds,
} from './utils/summaryCalculations';
import {
  AuthPage,
  BreakdownView,
  ContactView,
  FaqPage,
  IssuesView,
  LandingView,
  PicksView,
  SearchView,
  SettingsView,
  StrategyView,
  SummaryView,
} from './views';
import { ActivityFeedView } from './views/ActivityFeedView';
import { AdminView } from './views/admin';
import { UserMarketPerformanceView } from './views/analytics/UserMarketPerformanceView';
import { CreatorEarningsModal } from './views/creator-earnings';
import { LabView } from './views/lab/LabView';
import { LeaderboardView } from './views/leaderboard';
import { ParlaysView } from './views/parlays';
import { OptimizerModal } from './views/picks/components/OptimizerModal';
import { optimizedParlayToNewParlay } from './views/picks/components/optimizerTrack';
import { setPlayerIdMap } from './views/picks/utils';
import { ResetPasswordPage } from './views/ResetPasswordPage';
import { VerifyEmailPage } from './views/VerifyEmailPage';

// =============================================================================
// Constants
// =============================================================================

/** Delay before re-fetching user after Stripe redirect, to allow webhook processing */
const STRIPE_WEBHOOK_REFETCH_DELAY_MS = 5000;

// =============================================================================
// Components
// =============================================================================

function AppContent({
  trackedParlaysState,
}: {
  trackedParlaysState: UseTrackedParlaysResult;
}): ReactElement {
  const { needsOnboarding, user, updatePreferences, authFetch } = useAuth();
  const { isHidden: isFeatureHidden } = useFeatureAccess();
  const currentLeagueKey = useCurrentLeagueKey();
  const leagueSportsbook = useLeagueSportsbook(currentLeagueKey);
  const [activeTab, setActiveTab] = useState<TabId>('picks');
  const [showSettings, setShowSettings] = useState(false);
  const [showEarningsDashboard, setShowEarningsDashboard] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(true);
  // Shared analytics filter state (persists across Summary/Breakdown/Issues tabs)
  const [analyticsFilter, setAnalyticsFilter] = useState<AnalyticsFilter>('all_tracked');
  const [analysisLevel, setAnalysisLevel] = useState<AnalysisLevel>('legs');
  // Share parlay modal state
  const [shareParlayId, setShareParlayId] = useState<number | null>(null);
  const [shareParlayName, setShareParlayName] = useState('');

  // Optimizer modal — state managed by Zustand store (useOptimizerStore)
  const optimizerIsOpen = useOptimizerStore((s) => s.isOpen);
  const closeOptimizer = useOptimizerStore((s) => s.close);
  const optimizerLegs = useOptimizerStore((s) => s.legs);
  const optimizerLoading = useOptimizerStore((s) => s.loading);
  const optimizerError = useOptimizerStore((s) => s.error);
  const optimizerResult = useOptimizerStore((s) => s.result);
  const optimizerNarratives = useOptimizerStore((s) => s.narratives);
  const optimizerEnrichingNarrative = useOptimizerStore((s) => s.enrichingNarrative);

  const builderLegCount = useParlayBuilderStore((s) => s.legs.length);
  const { legs, loading: legsLoading } = useLegs();
  const { bets, loading: betsLoading } = useBets(analyticsFilter);
  const { modePerformance, factorPerformance } = useBreakdownPerformance();
  const {
    trackedParlays,
    stats: trackedParlayStats,
    pagination: trackedParlayPagination,
    filters: trackedParlayFilters,
    setFilters: setTrackedParlayFilters,
    trackParlay,
    removeParlay,
    loadMore: loadMoreTrackedParlays,
    loadingMore: loadingMoreTrackedParlays,
    error: trackedParlayError,
    refetch: refetchTrackedParlays,
  } = trackedParlaysState;

  // The one copy of received shares on web: the header and nav badges and the Activity view read it
  const receivedShares = useSharing({ autoLoadReceived: true });

  // Version check — detect new deploys and prompt user to refresh
  const { hasNewVersion, dismiss: dismissVersionBanner } = useVersionCheck();

  // Feature flags — filter leaderboard tab when kill switch is off (admin bypasses)
  const featureFlags = useFeatureFlags();
  const isAdmin = user?.role === 'admin';
  const showLeaderboard = featureFlags.leaderboard || isAdmin;

  // A push, an email or a redirect opens the app at /parlays/<id>, /notifications, /settings or /admin
  const openSettings = useCallback(() => setShowSettings(true), []);
  const notificationLink = useNotificationLink({
    myBets: trackedParlaysState,
    setActiveTab,
    openSettings,
    isAdmin,
  });
  // While flags are still loading, treat leaderboard as visible — prevents
  // a cold-render race where DEFAULT_FEATURE_FLAGS.leaderboard=false briefly
  // hides the tab + redirects users away before the real flags arrive.
  const leaderboardTabVisible = showLeaderboard || featureFlags.isLoading;
  const visiblePrimaryTabs = useMemo(
    () => PRIMARY_TABS.filter((tab) => tab.id !== 'leaderboard' || leaderboardTabVisible),
    [leaderboardTabVisible],
  );

  // Redirect away from leaderboard tab if it becomes unavailable.
  // Skip during the initial flag fetch to avoid bouncing users before
  // real feature flags have loaded.
  useEffect(() => {
    if (featureFlags.isLoading) return;
    if (activeTab === 'leaderboard' && !showLeaderboard) {
      setActiveTab('picks');
    }
  }, [activeTab, showLeaderboard, featureFlags.isLoading]);

  // Listen for chat navigation events (AI chat requests tab switches)
  useEffect(() => {
    const validTabIds: Set<string> = new Set([
      'picks',
      'parlays',
      'lab',
      'leaderboard',
      'search',
      'feed',
      'admin',
    ]);
    const handler = (e: Event) => {
      const tab = (e as CustomEvent<{ tab: string }>).detail?.tab;
      if (tab === 'settings') {
        setShowSettings(true);
      } else if (tab && validTabIds.has(tab)) {
        setActiveTab(tab as TabId);
      }
    };
    window.addEventListener('chat-navigate-tab', handler);
    return () => window.removeEventListener('chat-navigate-tab', handler);
  }, []);

  // Handle post-checkout redirect query params from Stripe
  const { showToast } = useToast();
  const { refetchUser } = useAuth();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const upgradeStatus = params.get('upgrade');
    const verified = params.get('verified');

    const billingReturn = params.get('billing');

    if (upgradeStatus === 'success') {
      showToast('Subscription activated! Welcome to your new plan.', 'success', 6000);
      window.history.replaceState({}, '', window.location.pathname);
      refetchUser();
      setTimeout(() => refetchUser(), STRIPE_WEBHOOK_REFETCH_DELAY_MS);
    } else if (billingReturn === 'return') {
      showToast('Billing updated. Your account is refreshing...', 'info', 4000);
      window.history.replaceState({}, '', window.location.pathname);
      refetchUser();
      setTimeout(() => refetchUser(), STRIPE_WEBHOOK_REFETCH_DELAY_MS);
    } else if (upgradeStatus === 'canceled') {
      showToast('Checkout canceled. No charges were made.', 'info', 4000);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (verified === 'true') {
      showToast('Email verified! You now have access to all features.', 'success', 6000);
      window.history.replaceState({}, '', window.location.pathname);
      refetchUser(); // Update user state to reflect verification
    }
  }, [showToast, refetchUser]);

  // Optimizer modal state is in useOptimizerStore (Zustand — no event listener needed)

  // Memoize analytics computations — only recalculate when bets/legs/filter change,
  // not on every tab switch or unrelated state change
  const stats = useMemo(() => calculateBetStats(bets), [bets]);
  const byLeague = useMemo(() => groupByLeague(bets), [bets]);
  const byLegs = useMemo(() => groupByLegs(bets), [bets]);
  const byOdds = useMemo(() => groupByOdds(bets), [bets]);

  // Transform mode performance array into Record<string, GroupStats> for TableSection
  const byMode = useMemo(() => {
    if (modePerformance.length === 0) return undefined;
    const result: Record<
      string,
      { wagered: number; returned: number; wins: number; total: number }
    > = {};
    for (const m of modePerformance) {
      result[m.mode] = {
        wagered: 0,
        returned: 0,
        wins: m.hits,
        total: m.total,
      };
    }
    return result;
  }, [modePerformance]);

  // Compute leg-level stats for "legs" analysis level, filtered by analytics scope
  const filteredLegsForAnalytics = useMemo(
    () =>
      (analyticsFilter === 'real_bets' ? legs.filter((l) => l.origin === 'bet') : legs).map(
        (l) => ({
          ...l,
          over: l.overUnder === 'Over' ? true : l.overUnder === 'Under' ? false : undefined,
        }),
      ),
    [legs, analyticsFilter],
  );
  const legStats = useMemo(
    () => calculateLegStats(filteredLegsForAnalytics),
    [filteredLegsForAnalytics],
  );

  // Render non-stateful views (can remount without issue)
  const renderSimpleView = (): ReactElement | null => {
    switch (activeTab) {
      case 'leaderboard':
        return showLeaderboard ? <LeaderboardView /> : null;
      case 'search':
        return <SearchView />;
      case 'feed':
        return (
          <ActivityFeedView
            receivedShares={receivedShares}
            onNavigateToParlay={notificationLink.openParlay}
            linkedSharedParlayId={notificationLink.linkedSharedParlayId}
            onLinkedShareHandled={notificationLink.onLinkedShareHandled}
          />
        );
      case 'admin':
        return <AdminView />;
      case 'summary':
        return (
          <SummaryView
            stats={stats}
            legStats={legStats}
            loading={betsLoading || legsLoading}
            filter={analyticsFilter}
            onFilterChange={setAnalyticsFilter}
            level={analysisLevel}
            onLevelChange={setAnalysisLevel}
          />
        );
      case 'breakdown':
        return (
          <BreakdownView
            byLeague={byLeague}
            byLegs={byLegs}
            byOdds={byOdds}
            byMode={byMode}
            legStats={legStats}
            factorPerformance={factorPerformance}
            dateRange={stats.dateRange}
            filter={analyticsFilter}
            onFilterChange={setAnalyticsFilter}
            level={analysisLevel}
            onLevelChange={setAnalysisLevel}
          />
        );
      case 'issues':
        return <IssuesView filter={analyticsFilter} onFilterChange={setAnalyticsFilter} />;
      case 'strategy':
        return <StrategyView />;
      case 'market':
        return <UserMarketPerformanceView />;
      default:
        return null;
    }
  };

  // Check if we're on a stateful tab
  const isStatefulTab = activeTab === 'picks' || activeTab === 'parlays' || activeTab === 'lab';

  const contentMaxWidth =
    activeTab === 'picks' ||
    activeTab === 'parlays' ||
    activeTab === 'lab' ||
    activeTab === 'leaderboard' ||
    activeTab === 'feed' ||
    activeTab === 'search' ||
    activeTab === 'admin'
      ? 'max-w-7xl'
      : 'max-w-5xl';

  return (
    <div className="min-h-screen bg-theme-bg text-theme-text">
      {/* Sticky Header — always max-w-7xl so nav row never compresses on analytics tabs */}
      {/* The header's notification bell opens a card's parlay with the same rule as a link */}
      <OpenParlayContext.Provider value={notificationLink.openParlay}>
        <AppHeader
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          visiblePrimaryTabs={visiblePrimaryTabs}
          shareUnviewedCount={receivedShares.unviewedCount}
          onOpenSettings={() => setShowSettings(true)}
          onOpenMobileNav={() => setShowMobileNav(true)}
          isAdmin={isAdmin}
        />
      </OpenParlayContext.Provider>

      {/* Content container — width varies by tab */}
      <div className={`mx-auto ${contentMaxWidth} px-3 sm:px-6`}>
        {/* Cancel confirmation is now inline in PicksLoadingModal */}

        {/* Dispute banner — shown when user has an active or lost payment dispute */}
        {user?.disputeStatus && (
          <div className="pt-4">
            <DisputeBanner disputeStatus={user.disputeStatus} />
          </div>
        )}

        {/* Content area */}
        <div className={`pt-6 ${builderLegCount > 0 ? 'pb-24 sm:pb-20' : 'pb-6'}`}>
          {/* Simple views - remount on tab switch */}
          {!isStatefulTab && renderSimpleView()}

          {/* Stateful views - stay mounted, hidden when inactive */}
          <div className={activeTab === 'picks' ? '' : 'hidden'}>
            <PicksView
              legs={legs}
              trackedParlays={trackedParlays}
              trackedParlayStats={trackedParlayStats}
              trackParlay={trackParlay}
              onNavigateToParlays={() => setActiveTab('parlays')}
            />
          </div>
          <div className={activeTab === 'parlays' ? '' : 'hidden'}>
            <ParlaysView
              trackedParlays={trackedParlays}
              stats={trackedParlayStats}
              pagination={trackedParlayPagination}
              filters={trackedParlayFilters || {}}
              onFiltersChange={setTrackedParlayFilters}
              onRemove={removeParlay}
              onShare={(parlay) => {
                setShareParlayId(parlay.id);
                setShareParlayName(parlay.tier || 'Parlay');
              }}
              onLoadMore={loadMoreTrackedParlays}
              loadingMore={loadingMoreTrackedParlays}
              loadError={trackedParlayError}
              onRefresh={refetchTrackedParlays}
              onSettlementComplete={refetchTrackedParlays}
              openParlayId={notificationLink.openParlayId}
              onOpenParlayHandled={notificationLink.onOpenParlayHandled}
            />
          </div>
          {!isFeatureHidden('lab_view') && (
            <div className={activeTab === 'lab' ? '' : 'hidden'}>
              <LabView />
            </div>
          )}
        </div>
      </div>
      <ParlayBuilderPanel trackParlay={trackParlay} />
      {optimizerIsOpen && (
        <OptimizerModal
          isOpen={optimizerIsOpen}
          onClose={closeOptimizer}
          legs={optimizerLegs}
          loading={optimizerLoading}
          error={optimizerError}
          result={optimizerResult}
          onOptimize={async (config) => {
            const store = useOptimizerStore.getState();
            store.setLoading(true);
            try {
              const res = await authFetch(apiUrl('/api/ml/optimize-parlays'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ legs: store.legs, config }),
              });
              if (!res.ok) {
                const err = await res.json().catch(() => ({ error: 'Optimization failed' }));
                store.setError(err.error ?? 'Optimization failed');
                return;
              }
              store.setResult(await res.json());
            } catch (err) {
              store.setError(err instanceof Error ? err.message : 'Optimization failed');
            }
          }}
          onClearResult={() => useOptimizerStore.getState().setResult(null)}
          onTrackParlay={async (parlayIndex) => {
            const store = useOptimizerStore.getState();
            const parlay = store.result?.parlays[parlayIndex];
            if (!parlay) return;
            try {
              // trackParlay reports a rejection in its result rather than throwing
              const result = await trackParlay(optimizedParlayToNewParlay(parlay, store.legs));
              if (result.success) showToast(`${parlay.tier} tracked!`, 'success');
              else showToast(result.error ?? 'Failed to track parlay', 'error');
            } catch {
              showToast('Failed to track parlay', 'error');
            }
          }}
          onTrackAll={async () => {
            const store = useOptimizerStore.getState();
            if (!store.result) return;
            let tracked = 0;
            for (const parlay of store.result.parlays) {
              try {
                const result = await trackParlay(optimizedParlayToNewParlay(parlay, store.legs));
                if (result.success) tracked++;
              } catch {
                /* continue tracking others */
              }
            }
            const total = store.result.parlays.length;
            showToast(
              tracked === total
                ? `${tracked} parlays tracked!`
                : `${tracked} of ${total} parlays tracked`,
              tracked === total ? 'success' : 'error',
            );
          }}
          onEnrichNarrative={async () => {
            const store = useOptimizerStore.getState();
            if (!store.result) return;
            store.setEnrichingNarrative(true);
            try {
              const res = await authFetch(apiUrl('/api/ml/enrich-narrative'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  parlays: store.result.parlays.map((p) => ({
                    tier: p.tier,
                    combinedOdds: p.combinedOdds,
                    isAnchorParlay: p.isAnchorParlay,
                    legs: p.legs.map((l) => ({
                      player: l.player ?? '',
                      statType: l.statType,
                      line: l.line,
                      overUnder: l.overUnder,
                      odds: l.odds,
                    })),
                    templateReasoning: '',
                  })),
                  slateType: store.result.metadata.slateType,
                }),
              });
              if (res.ok) {
                const data = await res.json();
                store.setNarratives(data.parlayNarratives ?? []);
              } else {
                store.setEnrichingNarrative(false);
                showToast('Failed to enrich parlays', 'error');
              }
            } catch {
              store.setEnrichingNarrative(false);
              showToast('Failed to enrich parlays', 'error');
            }
          }}
          narratives={optimizerNarratives}
          enrichingNarrative={optimizerEnrichingNarrative}
        />
      )}
      <GlobalModals />
      <MobileNav
        isOpen={showMobileNav}
        onClose={() => setShowMobileNav(false)}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        primaryTabs={visiblePrimaryTabs}
        analyticsTabs={ANALYTICS_TABS}
        isAdmin={user?.role === 'admin'}
        sportsbook={leagueSportsbook.sportsbook}
        onSportsbookChange={leagueSportsbook.setSportsbook}
        shareUnviewedCount={receivedShares.unviewedCount}
        currentLeague={currentLeagueKey}
        onLeagueChange={(key) => updatePreferences({ preferredLeague: key })}
      />
      {showSettings && (
        <SettingsView
          onClose={() => setShowSettings(false)}
          onOpenEarningsDashboard={() => {
            setShowSettings(false);
            setShowEarningsDashboard(true);
          }}
        />
      )}
      {showEarningsDashboard && (
        <CreatorEarningsModal onClose={() => setShowEarningsDashboard(false)} />
      )}
      {shareParlayId !== null && (
        <ShareParlayModal
          parlayId={shareParlayId}
          parlayName={shareParlayName}
          onClose={() => setShareParlayId(null)}
        />
      )}
      {hasNewVersion && (
        <AppVersionBanner
          onRefresh={() => window.location.reload()}
          onDismiss={dismissVersionBanner}
        />
      )}
      {needsOnboarding && showOnboarding && (
        <OnboardingModal
          onClose={() => setShowOnboarding(false)}
          onNavigateToPicks={() => setActiveTab('picks')}
        />
      )}
    </div>
  );
}

function AppRouter(): ReactElement {
  const {
    isAuthenticated,
    user,
    loading,
    logoutReason,
    clearLogoutReason,
    resendVerificationEmail,
  } = useAuth();
  // Owned here so the chat panel tracks through the same list the app shows
  const trackedParlaysState = useTrackedParlays();
  // Load dynamic player ID map for headshot URLs
  useEffect(() => {
    fetch(apiUrl('/api/players/ids'))
      .then((res) => res.json())
      .then((data) => {
        if (data && typeof data === 'object') {
          // API returns { nba: Record<string, number>, wnba: Record<string, number> }.
          // Pass through unchanged — setPlayerIdMap routes each league to the
          // right map (activePlayerIdMap / activeWnbaPlayerIdMap) so headshot
          // URLs hit the correct CDN. The previous version flattened both
          // leagues into one record, which silently triggered the legacy-flat
          // branch and dropped every WNBA ID into the NBA map. WNBA URLs then
          // resolved to cdn.nba.com (a generic silhouette placeholder) instead
          // of cdn.wnba.com (the real photo).
          setPlayerIdMap(data);
        }
      })
      .catch(() => {
        // Silent failure - falls back to hardcoded player ID map
      });
  }, []);

  // Cmd+K / Ctrl+K to toggle chat panel
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        useChatStore.getState().toggle();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  if (loading) {
    return (
      <div
        data-testid="app-boot-loader"
        className="min-h-screen bg-theme-bg flex items-center justify-center"
      >
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-theme-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-theme-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {isAuthenticated && user && !user.emailVerified ? (
        <EmailVerificationGate email={user.email} onResend={resendVerificationEmail} />
      ) : isAuthenticated ? (
        <>
          {/* Flex row: main content + chat sidebar (desktop only) */}
          <div className="md:flex md:h-screen md:overflow-hidden">
            <div className="@container flex-1 min-w-0 md:overflow-y-auto">
              {/* One unread count and live connection for the bell and the Activity view */}
              <NotificationsProvider>
                <AppContent trackedParlaysState={trackedParlaysState} />
              </NotificationsProvider>
            </div>
            {/* AI Chat Assistant — Elite+ only, flex sidebar on desktop */}
            <ChatTrackContext.Provider value={trackedParlaysState.trackParlay}>
              <ChatPanel />
            </ChatTrackContext.Provider>
          </div>
          {/* Modal for users who need to re-accept updated legal documents */}
          <LegalReAcceptanceModal />
          <ChatFloatingButton />
        </>
      ) : (
        <LandingView />
      )}
      {/* Blocking modal when user is logged out due to account pause/deletion */}
      {logoutReason && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="bg-theme-surface rounded-2xl p-6 border border-theme-border w-full max-w-sm shadow-xl text-center">
            {logoutReason === 'paused' ? (
              <PauseCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            ) : (
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            )}
            <h3
              className={`text-lg font-semibold mb-2 ${logoutReason === 'paused' ? 'text-amber-500' : 'text-red-500'}`}
            >
              {LOGOUT_REASON_MESSAGES[logoutReason].title}
            </h3>
            <p className="text-sm text-theme-text-secondary mb-4">
              {LOGOUT_REASON_MESSAGES[logoutReason].description}
            </p>
            <button
              type="button"
              onClick={clearLogoutReason}
              className="w-full py-2.5 bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill font-medium rounded-lg transition-colors"
            >
              Go to Login
            </button>
          </div>
        </div>
      )}
      <QuotaCoinNotification />
    </>
  );
}

/**
 * ScrollToTop Component
 * Scrolls window to top on route changes
 */
function ScrollToTop(): null {
  const { pathname } = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname dependency is intentional to trigger on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

/**
 * Profile Route Component
 * Handles /profile/:displayName URLs by opening UserProfileModal
 */
function ProfileRoute(): ReactElement | null {
  const { displayName } = useParams<{ displayName: string }>();
  const navigate = useNavigate();

  if (!displayName) {
    navigate('/');
    return null;
  }

  return (
    <UserProfileModal displayName={decodeURIComponent(displayName)} onClose={() => navigate('/')} />
  );
}

export default function App(): ReactElement {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <QuotaCoinProvider>
            <ThemeSync>
              <BrowserRouter>
                <ErrorBoundary>
                  <ScrollToTop />
                  <Routes>
                    <Route path="/" element={<AppRouter />} />
                    <Route path="/login" element={<AuthPage />} />
                    <Route path="/register" element={<AuthPage />} />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/faq" element={<FaqPage />} />
                    <Route path="/contact" element={<ContactView />} />
                    <Route path="/profile/:displayName" element={<ProfileRoute />} />
                    {/* Catch-all: unknown routes fall back to the app's
                        router, which renders the authenticated shell or the
                        landing page. Prevents blank-screen 404s. */}
                    <Route path="*" element={<AppRouter />} />
                  </Routes>
                  {/* Outside Routes so toasts render on every route, /profile/:displayName included */}
                  <ToastContainer />
                </ErrorBoundary>
              </BrowserRouter>
            </ThemeSync>
          </QuotaCoinProvider>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
